import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Tags, Wand2, Loader2, Plus, Pencil, Check, X, Search } from 'lucide-react';
import Navbar from '../../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, tone, testid }) => (
  <div className="bg-card border border-border-light rounded-2xl p-4" data-testid={testid}>
    <p className={`text-2xl font-semibold ${tone || 'text-text-primary'}`}>{value ?? '—'}</p>
    <p className="text-xs text-text-muted mt-1">{label}</p>
  </div>
);

const CategoryRow = ({ c, onSave }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(c.name);
  return (
    <li data-testid={`food-cat-row-${c.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-border-light first:border-t-0">
      <div className="flex-1 min-w-[160px]">
        {edit ? (
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid={`food-cat-name-input-${c.id}`}
            className="w-full px-2 py-1 border border-border-light rounded-lg text-sm" />
        ) : (
          <p className={`text-sm font-medium ${c.is_active ? 'text-text-primary' : 'text-text-muted line-through'}`}>{c.name}</p>
        )}
        <p className="text-xs text-text-muted">{c.items_count} dish{c.items_count === 1 ? '' : 'es'}</p>
      </div>
      {!c.locked && (edit ? (
        <>
          <button onClick={() => { onSave(c, { name }); setEdit(false); }} data-testid={`food-cat-save-${c.id}`} className="p-2 rounded-lg text-emerald-700 hover:bg-emerald-50"><Check className="h-4 w-4" /></button>
          <button onClick={() => { setName(c.name); setEdit(false); }} className="p-2 rounded-lg text-text-muted hover:bg-background"><X className="h-4 w-4" /></button>
        </>
      ) : (
        <>
          <button onClick={() => setEdit(true)} data-testid={`food-cat-edit-${c.id}`} className="p-2 rounded-lg text-primary hover:bg-primary-light"><Pencil className="h-4 w-4" /></button>
          <button onClick={() => onSave(c, { is_active: !c.is_active })} data-testid={`food-cat-toggle-${c.id}`}
            className={`text-xs px-2.5 py-1 rounded-full border ${c.is_active ? 'border-emerald-300 text-emerald-700' : 'border-border-light text-text-muted'}`}>
            {c.is_active ? 'On' : 'Off'}
          </button>
        </>
      ))}
      {c.locked && <span className="text-[11px] text-text-muted">Always on</span>}
    </li>
  );
};

const CategoryManager = ({ cats, reload, setMsg }) => {
  const [newName, setNewName] = useState('');
  const save = async (c, body) => {
    if (body.is_active === false && !window.confirm(`Switch off "${c.name}"? Its ${c.items_count} dishes move to Other and appear in Needs checking.`)) return;
    try {
      await axios.patch(`${API}/admin/food-categories/${c.id}`, body, { withCredentials: true });
      setMsg('Category updated'); reload();
    } catch (e) { setMsg(e.response?.data?.detail || 'Update failed'); }
  };
  const add = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/admin/food-categories`, { name: newName.trim() }, { withCredentials: true });
      setNewName(''); setMsg('Category added'); reload();
    } catch (e) { setMsg(e.response?.data?.detail || 'Could not add'); }
  };
  return (
    <div className="bg-card border border-border-light rounded-2xl" data-testid="food-cat-manager">
      <div className="px-4 py-3 border-b border-border-light flex items-center gap-2"><Tags className="h-4 w-4 text-primary" /><h2 className="font-semibold text-sm">Categories</h2></div>
      <ul>{cats.map((c) => <CategoryRow key={c.id} c={c} onSave={save} />)}</ul>
      <div className="flex gap-2 p-3 border-t border-border-light">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New category name" data-testid="food-cat-new-name"
          className="flex-1 min-w-0 px-3 py-2 border border-border-light rounded-lg text-sm" />
        <button onClick={add} data-testid="food-cat-add-btn" className="px-3 py-2 rounded-lg bg-primary text-white text-sm flex items-center gap-1"><Plus className="h-4 w-4" /> Add</button>
      </div>
    </div>
  );
};

const ReviewRow = ({ it, cats, checked, onCheck, onSet }) => (
  <tr data-testid={`review-row-${it.id}`} className="border-t border-border-light">
    <td className="px-3 py-2"><input type="checkbox" checked={checked} onChange={() => onCheck(it.id)} data-testid={`review-check-${it.id}`} /></td>
    <td className="px-3 py-2">
      <p className="text-sm font-medium text-text-primary break-words">{it.name}</p><p className="text-xs text-text-muted">{it.vendor_name}</p>
      <p className="text-[11px] text-text-muted sm:hidden mt-0.5">Guess: {it.suggestion ? `${it.suggestion}${it.confidence != null ? ` · ${Math.round(it.confidence * 100)}%` : ''}` : 'none'}</p>
    </td>
    <td className="px-3 py-2 text-xs hidden sm:table-cell" data-testid={`review-suggestion-${it.id}`}>
      {it.suggestion ? <span>{it.suggestion}{it.confidence != null && <span className="text-text-muted"> · {Math.round(it.confidence * 100)}%</span>}</span> : <span className="text-text-muted">No guess</span>}
    </td>
    <td className="px-3 py-2">
      <div className="flex items-center gap-1.5">
        <select value={it.needs_review ? '' : it.food_category} onChange={(e) => e.target.value && onSet([it.id], e.target.value)} data-testid={`review-select-${it.id}`}
          className="text-xs px-2 py-1 border border-border-light rounded-lg bg-background max-w-[140px]">
          {it.needs_review && <option value="">Pick…</option>}
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {it.needs_review && it.suggestion && (
          <button onClick={() => onSet([it.id], it.suggestion)} title={`Accept ${it.suggestion}`} data-testid={`review-accept-${it.id}`}
            className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-50"><Check className="h-4 w-4" /></button>
        )}
      </div>
      {it.source && <span className="block text-[10px] text-text-muted mt-0.5">by {it.source}</span>}
    </td>
  </tr>
);

export default function FoodCategories() {
  const [cats, setCats] = useState([]);
  const [ov, setOv] = useState(null);
  const [status, setStatus] = useState('needs_review');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState({ total: 0, items: [] });
  const [sel, setSel] = useState([]);
  const [bulkCat, setBulkCat] = useState('');
  const [msgState, setMsgState] = useState({ text: '', k: 0 });
  const msg = msgState.text;
  const setMsg = useCallback((text) => setMsgState({ text, k: Date.now() }), []);

  const loadCats = useCallback(async () => {
    const { data } = await axios.get(`${API}/food-categories?include_inactive=true`, { withCredentials: true });
    setCats(data);
  }, []);
  const loadOv = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/food-categories/overview`, { withCredentials: true });
    setOv(data); return data;
  }, []);
  const loadRows = useCallback(async () => {
    const q = new URLSearchParams({ status, limit: '300' });
    if (search.trim()) q.set('search', search.trim());
    const { data } = await axios.get(`${API}/admin/food-categories/review?${q}`, { withCredentials: true });
    setRows(data); setSel([]);
  }, [status, search]);

  useEffect(() => { loadCats(); loadOv(); }, [loadCats, loadOv]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { if (msgState.text) { const t = setTimeout(() => setMsgState({ text: '', k: 0 }), 3500); return () => clearTimeout(t); } return undefined; }, [msgState]);

  const running = ov?.job?.status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      const d = await loadOv();
      if (d?.job?.status !== 'running') { loadRows(); loadCats(); }
    }, 2500);
    return () => clearInterval(t);
  }, [running, loadOv, loadRows, loadCats]);

  const startAuto = async () => {
    try {
      await axios.post(`${API}/admin/food-categories/auto-tag`, { only_untagged: true, use_ai: true }, { withCredentials: true });
      setMsg('Auto-labelling started'); loadOv();
    } catch (e) { setMsg(e.response?.data?.detail || 'Could not start'); }
  };
  const bulk = async (ids, food_category) => {
    try {
      const { data } = await axios.post(`${API}/admin/food-categories/bulk-set`, { item_ids: ids, food_category }, { withCredentials: true });
      setMsg(`${data.updated} dish${data.updated === 1 ? '' : 'es'} updated${data.skipped_no_suggestion ? ` · ${data.skipped_no_suggestion} had no guess` : ''}`);
      loadRows(); loadOv(); loadCats();
    } catch (e) { setMsg(e.response?.data?.detail || 'Update failed'); }
  };
  const activeNames = cats.filter((c) => c.is_active).map((c) => c.name);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOn = rows.items.length > 0 && sel.length === rows.items.length;

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-text-primary">Food Categories</h1>
        <p className="text-sm text-text-secondary mt-1 mb-6">Label every dish so Sales Reports show real categories instead of "Other".</p>
        {msg && <div data-testid="food-cat-msg" className="mb-4 px-4 py-2.5 rounded-lg bg-primary-light text-primary text-sm">{msg}</div>}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Stat label="Total dishes" value={ov?.total_items} testid="food-cat-stat-total" />
          <Stat label="Labelled" value={ov?.tagged} tone="text-emerald-700" testid="food-cat-stat-tagged" />
          <Stat label="Still 'Other'" value={ov?.untagged} tone="text-amber-700" testid="food-cat-stat-untagged" />
          <Stat label="Needs checking" value={ov?.needs_review} tone="text-rose-700" testid="food-cat-stat-review" />
        </div>

        <div className="bg-card border border-border-light rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="food-cat-auto-panel">
          <Wand2 className="h-5 w-5 text-primary" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-medium text-text-primary">Auto-label dishes</p>
            <p className="text-xs text-text-muted">Uses word rules, then AI for unclear names. Clear guesses are saved; the rest go to "Needs checking". Dishes you set by hand are never changed.</p>
            {ov?.job?.status && (
              <p className="text-xs mt-1" data-testid="food-cat-job-status">
                Last run: <strong>{ov.job.status}</strong> · {ov.job.done || 0}/{ov.job.stats?.scanned || 0} checked
                {ov.job.stats && ` · ${ov.job.stats.rule} by rules · ${ov.job.stats.ai_saved} by AI · ${ov.job.stats.needs_review} to check`}
              </p>
            )}
          </div>
          <button onClick={startAuto} disabled={running} data-testid="food-cat-auto-btn"
            className="px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {running ? 'Labelling…' : 'Auto-label untagged dishes'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1"><CategoryManager cats={cats} reload={() => { loadCats(); loadOv(); }} setMsg={setMsg} /></div>
          <div className="lg:col-span-2 bg-card border border-border-light rounded-2xl" data-testid="food-cat-review">
            <div className="p-3 border-b border-border-light flex flex-wrap items-center gap-2">
              {[['needs_review', 'Needs checking'], ['untagged', "Still 'Other'"], ['auto', 'Auto-labelled'], ['all', 'All dishes']].map(([k, l]) => (
                <button key={k} onClick={() => setStatus(k)} data-testid={`review-tab-${k}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${status === k ? 'bg-primary text-white border-primary' : 'border-border-light text-text-secondary'}`}>{l}</button>
              ))}
              <div className="relative ml-auto">
                <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-text-muted" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search dish" data-testid="review-search"
                  className="pl-7 pr-2 py-1.5 border border-border-light rounded-lg text-xs w-40" />
              </div>
            </div>
            {sel.length > 0 && (
              <div className="p-3 bg-primary-light/40 border-b border-border-light flex flex-wrap items-center gap-2" data-testid="review-bulk-bar">
                <span className="text-xs font-medium">{sel.length} selected</span>
                <button onClick={() => bulk(sel, null)} data-testid="review-apply-suggestions" className="text-xs px-3 py-1.5 rounded-lg bg-primary text-white">Accept guesses</button>
                <select value={bulkCat} onChange={(e) => setBulkCat(e.target.value)} data-testid="review-bulk-select" className="text-xs px-2 py-1.5 border border-border-light rounded-lg">
                  <option value="">Set category…</option>
                  {activeNames.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button disabled={!bulkCat} onClick={() => bulk(sel, bulkCat)} data-testid="review-bulk-apply" className="text-xs px-3 py-1.5 rounded-lg border border-primary text-primary disabled:opacity-40">Apply</button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full sm:min-w-[560px]">
                <thead><tr className="text-[11px] uppercase tracking-wider text-text-muted text-left">
                  <th className="px-3 py-2"><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? [] : rows.items.map((i) => i.id))} data-testid="review-check-all" /></th>
                  <th className="px-3 py-2">Dish</th><th className="px-3 py-2 hidden sm:table-cell">App's guess</th><th className="px-3 py-2">Category</th>
                </tr></thead>
                <tbody>
                  {rows.items.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-text-muted" data-testid="review-empty">Nothing here 🎉</td></tr>}
                  {rows.items.map((it) => <ReviewRow key={it.id} it={it} cats={activeNames} checked={sel.includes(it.id)} onCheck={toggle} onSet={bulk} />)}
                </tbody>
              </table>
            </div>
            {rows.total > rows.items.length && <p className="text-xs text-text-muted p-3">Showing {rows.items.length} of {rows.total}. Use search to narrow down.</p>}
          </div>
        </div>
      </div>
    </>
  );
}
